/**
 * IssuesQueueCard — iter87.105
 *
 * Commons Console → Ops → Issues tab. Lists incoming reports from the
 * in-app "Report an Issue" flow. Filter by status/category, per-row
 * actions (assign notes, transition status, delete). Deflected rows are
 * hidden by default (they auto-closed via FAQ) but toggle-able for
 * effectiveness metrics.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { RefreshCw, Trash2, Check, Clock, Circle, MessageCircleWarning } from "lucide-react";

const CATEGORY_LABEL = {
    cant_find: "Can't find",
    feature_broken: "Feature broken",
    data_wrong: "Data wrong",
    subscription: "Subscription",
    suggestion: "Suggestion",
    other: "Other",
};

const STATUS_TRANSITIONS = ["open", "in_progress", "resolved", "closed", "duplicate"];

function _fmtDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })} · ${d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
    } catch { return iso; }
}

function StatusPill({ status }) {
    const map = {
        open: { icon: Circle, color: "text-cyber", border: "border-cyber/50" },
        in_progress: { icon: Clock, color: "text-amber-400", border: "border-amber-500/50" },
        resolved: { icon: Check, color: "text-green-400", border: "border-green-500/50" },
        closed: { icon: Check, color: "text-zinc-500", border: "border-zinc-600" },
        duplicate: { icon: Check, color: "text-zinc-500", border: "border-zinc-600" },
    };
    const cfg = map[status] || map.open;
    const IconEl = cfg.icon;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded border ${cfg.border} ${cfg.color}`}>
            <IconEl className="w-3 h-3" />
            {status}
        </span>
    );
}

export default function IssuesQueueCard() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [includeDeflected, setIncludeDeflected] = useState(false);
    const [expanded, setExpanded] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const arr = await api.listIssues({
                status: statusFilter || undefined,
                category: categoryFilter || undefined,
                include_deflected: includeDeflected,
            });
            setRows(Array.isArray(arr) ? arr : []);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load issues");
        }
        setLoading(false);
    }, [statusFilter, categoryFilter, includeDeflected]);

    useEffect(() => { load(); }, [load]);

    const counts = useMemo(() => {
        const c = { open: 0, in_progress: 0, resolved: 0, closed: 0, deflected: 0 };
        for (const r of rows) {
            if (r.deflected) c.deflected += 1;
            else c[r.status] = (c[r.status] || 0) + 1;
        }
        return c;
    }, [rows]);

    const changeStatus = async (id, next) => {
        if (busy) return;
        setBusy(true);
        try {
            await api.patchIssue(id, { status: next });
            toast.success(`Status → ${next}`);
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
        setBusy(false);
    };

    const deleteRow = async (id, title) => {
        if (!window.confirm(`Delete issue: ${title}?\n\nThis cannot be undone.`)) return;
        if (busy) return;
        setBusy(true);
        try {
            await api.deleteIssue(id);
            toast.success("Deleted");
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
        setBusy(false);
    };

    return (
        <div className="space-y-4" data-testid="issues-queue-card">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h2 className="font-display font-extrabold text-2xl uppercase flex items-center gap-2">
                        <MessageCircleWarning className="w-6 h-6 text-volt" />
                        Issues
                    </h2>
                    <p className="text-xs text-zinc-500 mt-1">
                        Bug reports and feature ideas filed via the in-app "Report an Issue" flow.
                        Deflected via FAQ: <span className="text-green-400 font-bold">{counts.deflected}</span>.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={load}
                    disabled={loading}
                    className="brutal-btn-ghost text-xs py-1.5 px-2 disabled:opacity-40"
                    data-testid="issues-refresh-btn"
                >
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                </button>
            </div>

            <div className="brutal-card p-3 flex flex-wrap gap-2 items-center">
                <span className="text-[10px] uppercase font-bold text-zinc-500">Filter:</span>
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="bg-[#0d0d0d] border border-[#222] rounded px-2 py-1 text-xs text-white"
                    data-testid="issues-status-filter"
                >
                    <option value="">All statuses</option>
                    {STATUS_TRANSITIONS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                    ))}
                </select>
                <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="bg-[#0d0d0d] border border-[#222] rounded px-2 py-1 text-xs text-white"
                    data-testid="issues-category-filter"
                >
                    <option value="">All categories</option>
                    {Object.entries(CATEGORY_LABEL).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                    ))}
                </select>
                <label className="flex items-center gap-1.5 text-[11px] text-zinc-400 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={includeDeflected}
                        onChange={(e) => setIncludeDeflected(e.target.checked)}
                        className="accent-cyber"
                        data-testid="issues-include-deflected"
                    />
                    Include deflected
                </label>
                <div className="text-[10px] text-zinc-500 ml-auto">
                    <span className="text-cyber font-bold">{counts.open}</span> open ·{" "}
                    <span className="text-amber-400 font-bold">{counts.in_progress}</span> in progress ·{" "}
                    <span className="text-green-400 font-bold">{counts.resolved}</span> resolved
                </div>
            </div>

            {loading && rows.length === 0 ? (
                <div className="text-xs text-zinc-500 p-4 border-2 border-dashed border-zinc-800 text-center">
                    Loading…
                </div>
            ) : rows.length === 0 ? (
                <div className="text-xs text-zinc-500 p-4 border-2 border-dashed border-zinc-800 text-center" data-testid="issues-empty">
                    No matching issues.
                </div>
            ) : (
                <div className="rounded border border-[#222] overflow-x-auto">
                    <table className="w-full text-xs" data-testid="issues-table">
                        <thead className="text-[10px] uppercase tracking-wider text-zinc-500 bg-[#0d0d0d]">
                            <tr>
                                <th className="text-left px-3 py-2">Title / Reporter</th>
                                <th className="text-left px-3 py-2">Category</th>
                                <th className="text-left px-3 py-2">Status</th>
                                <th className="text-left px-3 py-2">Filed</th>
                                <th className="text-right px-3 py-2">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <>
                                    <tr
                                        key={r.id}
                                        className={`border-t border-[#222] hover:bg-[#111]/60 cursor-pointer ${expanded === r.id ? "bg-cyber/5" : ""}`}
                                        onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                                        data-testid={`issues-row-${r.id}`}
                                    >
                                        <td className="px-3 py-2">
                                            <div className="text-white font-bold">{r.title}</div>
                                            <div className="text-[10px] text-zinc-500 mt-0.5">
                                                {r.reporter_email} {r.deflected && <span className="text-green-500 ml-1">· deflected</span>}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 text-zinc-300">{CATEGORY_LABEL[r.category] || r.category}</td>
                                        <td className="px-3 py-2"><StatusPill status={r.status} /></td>
                                        <td className="px-3 py-2 text-zinc-500 whitespace-nowrap">{_fmtDate(r.created_at)}</td>
                                        <td className="px-3 py-2 text-right whitespace-nowrap">
                                            <select
                                                value={r.status}
                                                onChange={(e) => { e.stopPropagation(); changeStatus(r.id, e.target.value); }}
                                                onClick={(e) => e.stopPropagation()}
                                                disabled={busy}
                                                className="bg-[#0d0d0d] border border-[#222] rounded px-1.5 py-0.5 text-[10px] text-white mr-1"
                                                data-testid={`issues-status-${r.id}`}
                                            >
                                                {STATUS_TRANSITIONS.map((s) => (
                                                    <option key={s} value={s}>{s}</option>
                                                ))}
                                            </select>
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); deleteRow(r.id, r.title); }}
                                                disabled={busy}
                                                className="brutal-btn-ghost text-[10px] py-1 px-2 text-zinc-500 hover:text-red-400 disabled:opacity-40"
                                                data-testid={`issues-delete-${r.id}`}
                                            >
                                                <Trash2 className="w-3 h-3" />
                                            </button>
                                        </td>
                                    </tr>
                                    {expanded === r.id && (
                                        <tr key={r.id + "-body"} className="bg-[#0a0a0a] border-t border-[#222]">
                                            <td colSpan={5} className="px-4 py-3">
                                                <div className="text-[11px] text-zinc-300 whitespace-pre-wrap leading-relaxed" data-testid={`issues-body-${r.id}`}>
                                                    {r.description}
                                                </div>
                                                <div className="mt-3 pt-3 border-t border-[#222] text-[10px] text-zinc-500 space-y-0.5">
                                                    <div>Page: <span className="text-zinc-400">{r.page_url || "—"}</span></div>
                                                    <div>League: <span className="text-zinc-400">{r.league_id || "—"}</span></div>
                                                    <div>Browser: <span className="text-zinc-400 font-mono">{r.browser_ua || "—"}</span></div>
                                                    <div>Tracking: <span className="text-cyber font-mono">{r.id.slice(0, 8).toUpperCase()}</span></div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
